import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HardDrive, Plus, Trash2, Download, Link2, Upload, Loader2, Pencil, X } from "lucide-react";
import { API } from "@/App";

// Panel admin « Mes scripts » : espace privé du fondateur pour retrouver le code
// complet de ses produits (VantaPhone téléphone/tablette, anticheat, autres).
// Chaque entrée = un fichier hébergé (≤ 20 Mo) ou un lien (pour les gros fichiers).

const errorText = (err, fallback) => err?.response?.data?.detail || fallback;
const fmtSize = (n) => (!n ? "" : n > 1048576 ? `${(n / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);
const EMPTY = { name: "", category: "", description: "", link: "", file_url: "", filename: "", size: 0 };

export default function FounderScripts({ hdr }) {
  const [scripts, setScripts] = useState(null);
  const [form, setForm] = useState(null); // null = fermé ; sinon {id?, ...}
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/scripts`, { headers: hdr() });
      setScripts(r.data.scripts);
    } catch (err) { toast.error(errorText(err, "Chargement impossible")); }
  }, [hdr]);
  useEffect(() => { load(); }, [load]);

  const pickFile = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Fichier trop gros (max 20 Mo) — utilise un lien à la place"); return; }
    setBusy(true);
    try {
      const data = await new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(file); });
      const r = await axios.post(`${API}/admin/upload/file`, { filename: file.name, data }, { headers: hdr() });
      setForm((f) => ({ ...f, file_url: r.data.url, filename: r.data.filename, size: r.data.size, link: "" }));
      toast.success("Fichier prêt");
    } catch (err) { toast.error(errorText(err, "Upload impossible")); }
    setBusy(false);
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (form.id) await axios.put(`${API}/admin/scripts/${form.id}`, form, { headers: hdr() });
      else await axios.post(`${API}/admin/scripts`, form, { headers: hdr() });
      toast.success("Enregistré");
      setForm(null);
      load();
    } catch (err) { toast.error(errorText(err, "Enregistrement impossible")); }
    setBusy(false);
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer « ${s.name} » de ton espace ? (le lien/fichier hébergé ici est retiré)`)) return;
    try { await axios.delete(`${API}/admin/scripts/${s.id}`, { headers: hdr() }); toast.success("Supprimé"); load(); }
    catch (err) { toast.error(errorText(err, "Suppression impossible")); }
  };

  const download = async (s) => {
    if (s.link) { window.open(s.link, "_blank", "noopener"); return; }
    try {
      const r = await axios.get(`${API}/files/${s.file_id}`, { headers: hdr(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = s.filename || `${s.name}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(errorText(err, "Téléchargement impossible")); }
  };

  if (!scripts) return <div className="text-sm text-white/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Chargement…</div>;
  return (
    <div className="space-y-4" data-testid="founder-scripts">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h2 className="text-lg font-semibold flex items-center gap-2"><HardDrive className="w-5 h-5 text-violet-300" />Mes scripts</h2>
          <p className="text-sm text-white/50">Ton espace privé (fondateur) : le code complet de tes produits, au même endroit. Fichier ≤ 20 Mo, ou un lien pour les gros.</p>
        </div>
        {!form && <button type="button" onClick={() => setForm({ ...EMPTY })} data-testid="add-script"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white text-black text-sm font-medium hover:bg-white/90"><Plus className="w-4 h-4" />Ajouter un script</button>}
      </div>

      {form && (
        <form onSubmit={save} className="border border-violet-500/30 bg-violet-500/5 rounded-xl p-4 space-y-3" data-testid="script-form">
          <div className="grid sm:grid-cols-2 gap-3">
            <input autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={80} placeholder="Nom (ex. VantaPhone — téléphone)" required
              className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-white/25" data-testid="script-name" />
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} maxLength={40} placeholder="Catégorie (Téléphone, Tablette, Anticheat…)"
              className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-white/25" />
          </div>
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500} rows={2} placeholder="Note (facultatif) : version, ce que contient le zip…"
            className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white resize-none outline-none focus:border-white/25" />
          <div className="flex flex-wrap items-center gap-2">
            <input type="file" ref={fileRef} className="hidden" onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ""; }} />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="script-upload"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/15 text-sm hover:bg-white/5 disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{form.file_url ? `Fichier : ${form.filename}` : "Choisir un fichier (≤ 20 Mo)"}</button>
            {form.file_url && <button type="button" onClick={() => setForm({ ...form, file_url: "", filename: "", size: 0 })} className="text-white/50 hover:text-red-300" title="Retirer le fichier"><X className="w-4 h-4" /></button>}
            <span className="text-xs text-white/35">ou</span>
            <div className="flex items-center gap-1.5 flex-1 min-w-[220px]">
              <Link2 className="w-4 h-4 text-white/40 shrink-0" />
              <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value, file_url: e.target.value ? "" : form.file_url })} placeholder="https://… (lien de téléchargement)"
                className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-white/25" data-testid="script-link" />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white text-black text-sm font-medium hover:bg-white/90 disabled:opacity-50" data-testid="script-save">Enregistrer</button>
            <button type="button" onClick={() => setForm(null)} className="px-3 py-2 rounded-lg border border-white/15 text-sm hover:bg-white/5">Annuler</button>
          </div>
        </form>
      )}

      {scripts.length === 0 && !form ? <p className="text-sm text-white/40">Aucun script pour l'instant. Clique sur « Ajouter un script ».</p> : (
        <div className="divide-y divide-white/5 border border-white/10 rounded-lg">
          {scripts.map((s) => (
            <div key={s.id} className="px-3 py-3 flex flex-wrap items-center gap-3" data-testid="script-row">
              <span className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0"><HardDrive className="w-4 h-4 text-white/50" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium flex items-center gap-2 flex-wrap">{s.name}
                  {s.category && <span className="text-[11px] px-1.5 py-0.5 rounded border border-white/10 text-white/50">{s.category}</span>}
                  {s.link ? <span className="text-[11px] text-sky-300/70">lien</span> : <span className="text-[11px] text-white/40">{s.filename} {fmtSize(s.size)}</span>}
                </p>
                {s.description && <p className="text-xs text-white/45 truncate">{s.description}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button type="button" onClick={() => download(s)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-black text-sm font-medium hover:bg-white/90" data-testid="script-download"><Download className="w-4 h-4" />Télécharger</button>
                <button type="button" onClick={() => setForm({ ...s })} className="inline-flex items-center px-2 py-1.5 rounded-lg border border-white/15 text-white/60 hover:bg-white/5" title="Modifier"><Pencil className="w-4 h-4" /></button>
                <button type="button" onClick={() => remove(s)} className="inline-flex items-center px-2 py-1.5 rounded-lg border border-white/15 text-white/60 hover:text-red-300 hover:bg-white/5" title="Supprimer" data-testid="script-delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
